/**
 * Sample receiving, acceptance, pieces, labels, custody and disposal.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING ANYTHING HERE.
 * ===========================================================================
 *
 * THIS FILE DESCRIBES A PHYSICAL OBJECT ARRIVING AT A COUNTER. It does not
 * describe what anybody does to that object afterwards. There is no input
 * field, reading, formula, calculation order, rounding rule, acceptance limit,
 * chart, axis, Excel cell mapping or specialised layout here, and there must
 * never be one — not for the five initial tests and not for any test.
 *
 * The distinction is sharper than it looks, and decision **D70** exists
 * because of it. "Concrete samples must record cube dimensions and cast date"
 * reads like receiving metadata and is not: it is a claim about what a
 * laboratory's method requires, it differs between standards, and a product
 * that enforced it would reject work a laboratory legitimately accepts. So the
 * per-type mandatory-field mechanism below ships **configurable and empty**,
 * and the only universally required facts are the facts of the delivery.
 *
 * Four further ideas govern these shapes, each an acceptance criterion rather
 * than a preference:
 *
 *  1. A SAMPLE ID IS ISSUED ON REGISTRATION, BEFORE ANY DECISION (D72). The
 *     arrival is a fact whatever the laboratory concludes about it. A rejected
 *     sample keeps its number, and numbers are never reused.
 *
 *  2. ACCEPTANCE IS A HISTORY, NOT A FIELD (D73). A held sample commonly
 *     becomes accepted later, and that is a sequence of decisions each with an
 *     actor, a timestamp and a reason. The current decision is the latest row.
 *
 *  3. PIECES ARE OPTIONAL AND NEVER IMPLIED (D74). A sample has pieces because
 *     somebody recorded them, never because its type suggests it should.
 *
 *  4. CUSTODY IS APPEND-ONLY, AND RECORDS WHAT THE SYSTEM WAS TOLD (D75).
 *     Registering a sample creates no movement claiming it arrived somewhere:
 *     the arrival *is* the registration, and a chain with an editable link is
 *     not a chain.
 */
import { SAMPLE_STATUS, sampleMayBeAssignedTests, } from './status.js';
// ---------------------------------------------------------------------------
// The acceptance decision
// ---------------------------------------------------------------------------
/**
 * What an inspector concluded about a sample.
 *
 * These are the four outcomes of a decision, which is why
 * `awaiting_inspection` is absent: that is the state of a sample nobody has
 * decided about yet, and "no decision" is not a decision anybody makes.
 */
export const SAMPLE_ACCEPTANCE_DECISION = {
    ACCEPTED: 'accepted',
    /** Workable, and something about it belongs on the report. */
    ACCEPTED_WITH_NOTES: 'accepted_with_notes',
    /** Not refused, but not yet workable. Usually pending client information. */
    HELD: 'held',
    REJECTED: 'rejected',
};
export const ALL_SAMPLE_ACCEPTANCE_DECISIONS = Object.freeze(Object.values(SAMPLE_ACCEPTANCE_DECISION));
/**
 * Decisions that cannot be recorded without a stated reason.
 *
 * A refusal or a hold with no cause is not something a client can answer or a
 * supervisor can overturn. Accepting needs no justification; withholding work
 * does.
 */
export const DECISIONS_REQUIRING_REASON = Object.freeze([
    SAMPLE_ACCEPTANCE_DECISION.HELD,
    SAMPLE_ACCEPTANCE_DECISION.REJECTED,
]);
export function decisionRequiresReason(decision) {
    return DECISIONS_REQUIRING_REASON.includes(decision);
}
/**
 * The status a sample carries once a decision is recorded.
 *
 * The mapping is deliberately one-to-one and total. The status column exists
 * so a register can be filtered without joining the decision history; it is a
 * projection of the latest decision and never an independent source of truth.
 */
export function statusForDecision(decision) {
    switch (decision) {
        case SAMPLE_ACCEPTANCE_DECISION.ACCEPTED:
            return SAMPLE_STATUS.ACCEPTED;
        case SAMPLE_ACCEPTANCE_DECISION.ACCEPTED_WITH_NOTES:
            return SAMPLE_STATUS.ACCEPTED_WITH_NOTES;
        case SAMPLE_ACCEPTANCE_DECISION.HELD:
            return SAMPLE_STATUS.HELD;
        case SAMPLE_ACCEPTANCE_DECISION.REJECTED:
            return SAMPLE_STATUS.REJECTED;
    }
}
/** Re-exported so callers reason about testability in one place. */
export { sampleMayBeAssignedTests };
// ---------------------------------------------------------------------------
// Receiving fields, and which of them a laboratory may insist on
// ---------------------------------------------------------------------------
/**
 * The receiving facts a laboratory may mark mandatory per sample type.
 *
 * Every one is a property of the DELIVERY, not of the material: who the client
 * calls it, where it came from, when and by whom it was taken, what state it
 * arrived in, where it was put. None is a measurement and none implies a
 * method (`CLAUDE.md` section 4).
 *
 * `sampleType`, `receivedAt`, `receivedFrom`, `quantity` and `quantityUnit`
 * are NOT in this list because they are required for every sample regardless
 * of type and regardless of configuration — a delivery with no stated quantity
 * or no arrival time is not a record of anything. A quantity without a unit is
 * not a fact either, which is why the two travel together.
 */
export const CONFIGURABLE_RECEIVING_FIELD = {
    CLIENT_REFERENCE: 'client_reference',
    SOURCE_DESCRIPTION: 'source_description',
    SAMPLED_AT: 'sampled_at',
    SAMPLED_BY: 'sampled_by',
    CONDITION_ON_RECEIPT: 'condition_on_receipt',
    STORAGE_LOCATION: 'storage_location',
};
export const ALL_CONFIGURABLE_RECEIVING_FIELDS = Object.freeze(Object.values(CONFIGURABLE_RECEIVING_FIELD));
/**
 * The default per-type rules for a new laboratory: NONE.
 *
 * This is decision **D70**, and the emptiness is the decision rather than an
 * omission. Seeding "concrete requires a cast date" would be inventing a
 * method requirement, so no sample type arrives with an extra mandatory field
 * and every rule is something a laboratory entered for itself.
 */
export const DEFAULT_SAMPLE_TYPE_FIELD_RULES = Object.freeze({});
/** `SMP-2026-000123` piece 4 becomes `SMP-2026-000123-P04`. */
export function formatPieceCode(identity) {
    const index = String(identity.pieceIndex).padStart(2, '0');
    return `${identity.sampleNumber}-P${index}`;
}
// ---------------------------------------------------------------------------
// Labels and barcodes
// ---------------------------------------------------------------------------
/**
 * How a label encodes the sample for a machine to read (decision **D68**).
 *
 * Two symbologies, each for the job it is actually good at:
 *
 *  - `code128` carries the Sample ID and NOTHING else. What laboratories own
 *    is a keyboard-wedge scanner that types what it reads, so a barcode
 *    holding just the Sample ID resolves to the right sample by typing it into
 *    the register's search box — exactly as a person would, with no hardware
 *    assumption at all. Code 128 handles the full alphanumeric-with-hyphens
 *    identifier, which the numeric-only symbologies cannot.
 *  - `qr` carries the richer payload, because that payload is for a camera and
 *    every laboratory already has a phone.
 */
export const BARCODE_SYMBOLOGY = {
    CODE128: 'code128',
    QR: 'qr',
};
export const ALL_BARCODE_SYMBOLOGIES = Object.freeze(Object.values(BARCODE_SYMBOLOGY));
/** The default for a new laboratory, editable afterwards. */
export const DEFAULT_BARCODE_SYMBOLOGY = BARCODE_SYMBOLOGY.CODE128;
/**
 * The default physical label size for a new laboratory, in millimetres.
 *
 * A default for a new organisation, not a hardcoded organisational value: it
 * lives in the backend only, and each laboratory edits its own copy
 * (`CLAUDE.md` section 6.1). 100 x 50 mm is the commonest stock size for
 * laboratory sample labels and is a starting point, not a constraint.
 */
export const DEFAULT_LABEL_WIDTH_MM = 100;
export const DEFAULT_LABEL_HEIGHT_MM = 50;
// ---------------------------------------------------------------------------
// Custody and movement
// ---------------------------------------------------------------------------
/**
 * A recorded change in where a sample is or who holds it (decision **D75**).
 *
 * There is no `received` movement. Registering a sample does not create a
 * custody event claiming it arrived somewhere — the arrival is the
 * registration itself, and inventing a movement nobody recorded would be the
 * system asserting a physical fact it does not have.
 */
export const SAMPLE_MOVEMENT_TYPE = {
    /** Moved to a different storage location within the laboratory. */
    LOCATION_CHANGE: 'location_change',
    /** Moved between departments. */
    DEPARTMENT_TRANSFER: 'department_transfer',
    /** Handed to a named person, who now holds it. */
    CUSTODY_HANDOVER: 'custody_handover',
    RETURNED_TO_CLIENT: 'returned_to_client',
    DISPOSED: 'disposed',
    /**
     * A correction to an earlier movement.
     *
     * Movements are never edited or deleted, so a mistake is fixed by recording
     * that it was a mistake. The original stays visible, which is the whole
     * point of an append-only chain.
     */
    CORRECTION: 'correction',
};
export const ALL_SAMPLE_MOVEMENT_TYPES = Object.freeze(Object.values(SAMPLE_MOVEMENT_TYPE));
/** Movement types that cannot be recorded without a stated reason or note. */
export const MOVEMENTS_REQUIRING_NOTE = Object.freeze([
    SAMPLE_MOVEMENT_TYPE.RETURNED_TO_CLIENT,
    SAMPLE_MOVEMENT_TYPE.DISPOSED,
    SAMPLE_MOVEMENT_TYPE.CORRECTION,
]);
export function movementRequiresNote(type) {
    return MOVEMENTS_REQUIRING_NOTE.includes(type);
}
// ---------------------------------------------------------------------------
// Retention and disposal
// ---------------------------------------------------------------------------
/**
 * The default retention period for a new laboratory: NULL — keep until
 * somebody disposes of it (decision **D71**).
 *
 * The same reasoning as the Phase 3 file-retention default, for a stronger
 * reason: a physical sample is evidence, it cannot be restored from a backup,
 * and a system that threw one away on a schedule nobody checked would destroy
 * the only thing capable of settling a dispute. So the default destroys
 * nothing, and what a configured period produces is a **due date and a list**,
 * never a deletion.
 */
export const DEFAULT_SAMPLE_RETENTION_DAYS = null;
/**
 * When a sample becomes due for disposal, or `null` when it never does.
 *
 * Computed rather than stored, so a laboratory that changes its retention
 * period does not have to migrate a column of stale dates — the same reasoning
 * as the derived work-order progress status (decision D56).
 */
export function disposalDueAt(receivedAt, retentionDays) {
    if (retentionDays === null)
        return null;
    const due = new Date(receivedAt.getTime());
    due.setUTCDate(due.getUTCDate() + retentionDays);
    return due;
}
// ---------------------------------------------------------------------------
// Dynamic sample types (0024, D296-D305)
// ---------------------------------------------------------------------------
/**
 * The data types an additional intake field may have (D297). Deliberately
 * small: no formula, condition, script or computed field, because an intake
 * field records what someone states about a delivery, never a test reading.
 */
export const SAMPLE_FIELD_TYPE = {
    TEXT: 'text',
    LONG_TEXT: 'long_text',
    NUMBER: 'number',
    DATE: 'date',
    TIME: 'time',
    DATETIME: 'datetime',
    SELECT: 'select',
    BOOLEAN: 'boolean',
};
export const ALL_SAMPLE_FIELD_TYPES = Object.freeze(Object.values(SAMPLE_FIELD_TYPE));
/**
 * The stable code of a sample type, the key of a field, the value of an
 * option: lower-case ASCII, fixed once created. Names and labels change;
 * these never do.
 */
export const STABLE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const OPTION_VALUE_PATTERN = /^[a-z0-9][a-z0-9_]{0,39}$/;
/**
 * A stable key suggested from a label: lower-case ASCII words joined by `_`.
 * Empty when the label has no Latin letter to start from (an Arabic-only
 * label); the caller then numbers it instead.
 */
export function suggestStableKey(label) {
    const slug = (label ?? '')
        .normalize('NFKD')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^[^a-z]+/, '')
        .replace(/_+$/, '')
        .slice(0, 40)
        .replace(/_+$/, '');
    return STABLE_KEY_PATTERN.test(slug) ? slug : '';
}
/** The core facts every sample has, which no sample type may redefine (D296). */
export const CORE_SAMPLE_FIELDS = [
    'sample_type',
    'description',
    'quantity',
    'quantity_unit',
    'client_reference',
    'notes',
    'source_description',
    'sampled_at',
    'sampled_by',
    'condition_on_receipt',
    'storage_location',
];
//# sourceMappingURL=samples.js.map